/**
 * Journey Document Manager Component
 * Allows admin to attach documents at specific journey stages
 * and add notes visible to agents
 */
import { useState, useEffect } from 'react';
import { 
  Upload, FileText, Download, Trash2, Plus, X, 
  MessageSquare, Clock, User, File, Loader2,
  ChevronDown, ChevronUp, Paperclip, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { applicationsAPI, downloadAuthenticatedFile } from '@/lib/api';

// Document types that can be attached at different stages
const DOCUMENT_TYPES = [
  { value: 'offer_letter', label: 'Offer Letter', stages: ['offer_issued', 'offer_accepted'] },
  { value: 'conditional_offer', label: 'Conditional Offer Letter', stages: ['offer_issued'] },
  { value: 'unconditional_offer', label: 'Unconditional Offer Letter', stages: ['uco'] },
  { value: 'cas_letter', label: 'CAS Letter', stages: ['cas_stage'] },
  { value: 'visa_approval', label: 'Visa Approval Letter', stages: ['visa_issued'] },
  { value: 'enrollment_confirmation', label: 'Enrollment Confirmation', stages: ['enrolment_completed'] },
  { value: 'payment_receipt', label: 'Payment Receipt', stages: ['deposit'] },
  { value: 'admission_letter', label: 'Admission Letter', stages: ['application_submitted', 'offer_issued'] },
  { value: 'other', label: 'Other Document', stages: [] },
];

// Stage labels
const STAGE_LABELS = {
  inquiry: 'Inquiry',
  lead_validated: 'Lead Validated',
  application_received: 'Application Received',
  application_submitted: 'Application Submitted',
  offer_issued: 'Offer Issued',
  offer_accepted: 'Offer Accepted',
  uco: 'UCO',
  deposit: 'Deposit',
  cas_stage: 'CAS Stage',
  refund: 'Refund',
  visa_issued: 'Visa Issued',
  enrolment_completed: 'Enrolled',
  commission_received: 'Commission',
};

const ALL_STAGES = Object.keys(STAGE_LABELS);

export const JourneyDocumentManager = ({ 
  application, 
  onUpdate,
  isAdmin = false,
  isAgent = false 
}) => {
  const [documents, setDocuments] = useState([]);
  const [agentNotes, setAgentNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showAddNoteDialog, setShowAddNoteDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [expanded, setExpanded] = useState(true);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    stage: '',
    documentType: '',
    notes: '',
    file: null
  });
  
  // Note form state
  const [noteText, setNoteText] = useState('');

  // Load documents and notes
  useEffect(() => {
    loadData();
  }, [application?.id]);

  const loadData = async () => {
    if (!application?.id) return;
    setLoading(true);
    try {
      const [docsRes, notesRes] = await Promise.all([
        applicationsAPI.getJourneyDocuments(application.id),
        (isAdmin || isAgent) ? applicationsAPI.getAgentNotes(application.id) : Promise.resolve({ data: { notes: [] } })
      ]);
      setDocuments(docsRes.data.documents || []);
      setAgentNotes(notesRes.data.notes || []);
    } catch (error) {
      console.error('Failed to load journey data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.stage || !uploadForm.documentType) {
      toast.error('Please fill all required fields');
      return;
    }

    setUploading(true);
    try {
      await applicationsAPI.attachJourneyDocument(
        application.id,
        uploadForm.stage,
        uploadForm.documentType,
        uploadForm.file,
        uploadForm.notes
      );
      toast.success('Document attached successfully');
      setShowUploadDialog(false);
      setUploadForm({ stage: '', documentType: '', notes: '', file: null });
      loadData();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // Handle document download
  const handleDownload = async (doc) => {
    try {
      await downloadAuthenticatedFile(
        applicationsAPI.downloadJourneyDocument(application.id, doc.id),
        doc.filename
      );
    } catch (error) {
      const msg = error?.message || 'Failed to download document';
      toast.error(msg);
    }
  };

  // Handle document delete
  const handleDelete = async (docId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    try {
      await applicationsAPI.deleteJourneyDocument(application.id, docId);
      toast.success('Document deleted');
      loadData();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  // Handle add note
  const handleAddNote = async () => {
    if (!noteText.trim()) {
      toast.error('Please enter a note');
      return;
    }

    setAddingNote(true);
    try {
      await applicationsAPI.addAgentNote(application.id, noteText);
      toast.success('Note added successfully');
      setShowAddNoteDialog(false);
      setNoteText('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  // Handle delete note
  const handleDeleteNote = async (noteId) => {
    if (!confirm('Are you sure you want to delete this note?')) return;
    try {
      await applicationsAPI.deleteAgentNote(application.id, noteId);
      toast.success('Note deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get document type label
  const getDocTypeLabel = (value) => {
    return DOCUMENT_TYPES.find(t => t.value === value)?.label || value;
  };

  // Group documents by stage
  const documentsByStage = documents.reduce((acc, doc) => {
    const stage = doc.stage || 'other';
    if (!acc[stage]) acc[stage] = [];
    acc[stage].push(doc);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="bg-[#e9ecef]/30 rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
            <Paperclip className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">Journey Documents & Notes</h3>
            <p className="text-sm text-gray-500">
              {documents.length} document{documents.length !== 1 ? 's' : ''} • {agentNotes.length} note{agentNotes.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              {/* Only B2B (agent-managed) applications show the Add Note button —
                  Direct applications have no agent to notify. */}
              {application?.agent_id && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => { e.stopPropagation(); setShowAddNoteDialog(true); }}
                  className="border-blue-500/50 text-blue-500 hover:bg-blue-500/10"
                  data-testid="add-agent-note-btn"
                  title="Send a note to the agent managing this application"
                >
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Add Note for Agent
                </Button>
              )}
              <Button
                size="sm"
                onClick={(e) => { e.stopPropagation(); setShowUploadDialog(true); }}
                className="bg-teal-500 hover:bg-yellow-600 text-gray-900"
                data-testid="attach-document-btn"
              >
                <Upload className="w-4 h-4 mr-1" />
                Attach Document
              </Button>
            </>
          )}
          {expanded ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
        </div>
      </div>

      {/* Content */}
      {expanded && (
        <div className="p-4 pt-0 space-y-6">
          {/* Documents Section */}
          {documents.length > 0 ? (
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Attached Documents
              </h4>
              <div className="grid gap-3">
                {Object.entries(documentsByStage).map(([stage, docs]) => (
                  <div key={stage} className="space-y-2">
                    <Badge className="bg-gray-200 text-gray-600 text-xs">
                      {STAGE_LABELS[stage] || stage}
                    </Badge>
                    {docs.map((doc) => (
                      <div 
                        key={doc.id}
                        className="flex items-center justify-between bg-white rounded-lg p-3 border border-white/5"
                      >
                        <div className="flex items-center gap-3">
                          <File className="w-8 h-8 text-teal-600" />
                          <div>
                            <p className="text-sm font-medium text-gray-800">{doc.filename}</p>
                            <p className="text-xs text-gray-500">
                              {getDocTypeLabel(doc.document_type)} • {formatDate(doc.uploaded_at)}
                            </p>
                            {doc.notes && (
                              <p className="text-xs text-gray-400 mt-1">📝 {doc.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownload(doc)}
                            className="text-gray-500 hover:text-gray-800"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(doc.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No documents attached yet</p>
            </div>
          )}

          {/* Agent Notes Section */}
          {(isAdmin || isAgent) && (
            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-medium text-gray-600 flex items-center gap-2 mb-3">
                <MessageSquare className="w-4 h-4" /> 
                Agent Notes
                {application?.agent_id ? (
                  <Badge className="bg-green-100 text-green-600 text-xs ml-2">For Agent</Badge>
                ) : (
                  <Badge className="bg-gray-200 text-gray-500 text-xs ml-2">No Agent</Badge>
                )}
              </h4>
              
              {agentNotes.length > 0 ? (
                <div className="space-y-3">
                  {agentNotes.map((note) => (
                    <div 
                      key={note.id}
                      className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{note.note}</p>
                          <p className="text-xs text-gray-500 mt-2 flex items-center gap-2">
                            <User className="w-3 h-3" /> {note.added_by_name || note.added_by}
                            <Clock className="w-3 h-3 ml-2" /> {formatDate(note.created_at)}
                          </p>
                        </div>
                        {isAdmin && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteNote(note.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 bg-[#f1f3f5]/30 rounded-lg">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No notes for agent yet</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800">Attach Journey Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-600">Stage *</Label>
              <Select 
                value={uploadForm.stage} 
                onValueChange={(v) => setUploadForm(prev => ({ ...prev, stage: v }))}
              >
                <SelectTrigger className="bg-[#e9ecef] border-gray-200 text-gray-800">
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent className="bg-[#e9ecef] border-gray-200">
                  {ALL_STAGES.map((stage) => (
                    <SelectItem key={stage} value={stage} className="text-gray-800 hover:bg-gray-200">
                      {STAGE_LABELS[stage]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-gray-600">Document Type *</Label>
              <Select 
                value={uploadForm.documentType} 
                onValueChange={(v) => setUploadForm(prev => ({ ...prev, documentType: v }))}
              >
                <SelectTrigger className="bg-[#e9ecef] border-gray-200 text-gray-800">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent className="bg-[#e9ecef] border-gray-200">
                  {DOCUMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value} className="text-gray-800 hover:bg-gray-200">
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-gray-600">File *</Label>
              <Input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => setUploadForm(prev => ({ ...prev, file: e.target.files?.[0] || null }))}
                className="bg-[#e9ecef] border-gray-200 text-gray-800 file:bg-gray-200 file:text-gray-800 file:border-0"
              />
              <p className="text-xs text-gray-400 mt-1">Accepted: PDF, DOC, DOCX, JPG, PNG (max 10MB)</p>
            </div>

            <div>
              <Label className="text-gray-600">Notes (optional)</Label>
              <Textarea
                value={uploadForm.notes}
                onChange={(e) => setUploadForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add any notes about this document..."
                className="bg-[#e9ecef] border-gray-200 text-gray-800"
                rows={2}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowUploadDialog(false)}
                className="border-gray-300 text-gray-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploading || !uploadForm.file || !uploadForm.stage || !uploadForm.documentType}
                className="bg-teal-500 hover:bg-yellow-600 text-gray-900"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={showAddNoteDialog} onOpenChange={setShowAddNoteDialog}>
        <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800">Add Note for Agent</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!application?.agent_id && (
              <div className="bg-teal-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-sm text-teal-600">⚠️ This application has no assigned agent. The note will be saved but won't be visible to anyone.</p>
              </div>
            )}
            <div>
              <Label className="text-gray-600">Note *</Label>
              <Textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Enter a note for the agent (e.g., status update, required action, important information)..."
                className="bg-[#e9ecef] border-gray-200 text-gray-800"
                rows={4}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowAddNoteDialog(false)}
                className="border-gray-300 text-gray-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddNote}
                disabled={addingNote || !noteText.trim()}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                {addingNote ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                Add Note
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JourneyDocumentManager;
